import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  FiGrid,
  FiBox,
  FiFileText,
  FiTruck,
  FiUsers,
  FiCreditCard,
  FiBarChart2,
  FiSettings,
  FiHelpCircle,
  FiLogOut,
  FiMoreVertical,
  FiNavigation,
} from "react-icons/fi";

import {
  clearAuthSession,
} from "../../utils/authSession";

import {
  hasPermissionForUser,
  setCurrentUser,
  useCurrentUser,
} from "../../utils/permissions";

const menuGroups = [
  {
    title: "MAIN",
    items: [
      {
        to: "/",
        label: "Dashboard",
        icon: FiGrid,
        moduleKey: "dashboard",
        theme: {
          from: "#2563eb",
          to: "#7c3aed",
          glow: "rgba(59, 130, 246, 0.48)",
          soft: "rgba(59, 130, 246, 0.16)",
          border: "rgba(96, 165, 250, 0.50)",
        },
      },
      {
        to: "/containers",
        label: "Containers",
        icon: FiBox,
        moduleKey: "containers",
        theme: {
          from: "#0891b2",
          to: "#06b6d4",
          glow: "rgba(6, 182, 212, 0.45)",
          soft: "rgba(6, 182, 212, 0.16)",
          border: "rgba(34, 211, 238, 0.48)",
        },
      },
      {
        to: "/tracking",
        label: "Live Tracking",
        icon: FiNavigation,
        moduleKey: "tracking",
        theme: {
          from: "#7c3aed",
          to: "#c026d3",
          glow: "rgba(168, 85, 247, 0.45)",
          soft: "rgba(168, 85, 247, 0.16)",
          border: "rgba(192, 132, 252, 0.50)",
        },
      },
      {
        to: "/invoices",
        label: "Invoices",
        icon: FiFileText,
        moduleKey: "invoices",
        theme: {
          from: "#4f46e5",
          to: "#6366f1",
          glow: "rgba(99, 102, 241, 0.45)",
          soft: "rgba(99, 102, 241, 0.16)",
          border: "rgba(129, 140, 248, 0.50)",
        },
      },
      {
        to: "/cost-price-list",
        label: "Freight Rates",
        icon: FiTruck,
        moduleKey: "cost-prices",
        theme: {
          from: "#ea580c",
          to: "#f59e0b",
          glow: "rgba(249, 115, 22, 0.45)",
          soft: "rgba(249, 115, 22, 0.16)",
          border: "rgba(251, 146, 60, 0.50)",
        },
      },
      {
        to: "/vendors",
        label: "Forwarders",
        icon: FiUsers,
        moduleKey: "vendors",
        theme: {
          from: "#0f766e",
          to: "#14b8a6",
          glow: "rgba(20, 184, 166, 0.43)",
          soft: "rgba(20, 184, 166, 0.16)",
          border: "rgba(45, 212, 191, 0.48)",
        },
      },
    ],
  },
  {
    title: "FINANCE",
    items: [
      {
        to: "/payments",
        label: "Payments",
        icon: FiCreditCard,
        moduleKey: "payments",
        theme: {
          from: "#059669",
          to: "#10b981",
          glow: "rgba(16, 185, 129, 0.43)",
          soft: "rgba(16, 185, 129, 0.16)",
          border: "rgba(52, 211, 153, 0.48)",
        },
      },
      {
        to: "/reports",
        label: "Reports",
        icon: FiBarChart2,
        moduleKey: "reports",
        theme: {
          from: "#db2777",
          to: "#9333ea",
          glow: "rgba(217, 70, 239, 0.42)",
          soft: "rgba(217, 70, 239, 0.15)",
          border: "rgba(232, 121, 249, 0.48)",
        },
      },
    ],
  },
  {
    title: "SYSTEM",
    items: [
      {
        to: "/settings",
        label: "Settings",
        icon: FiSettings,
        moduleKey: "settings",
        theme: {
          from: "#0284c7",
          to: "#2563eb",
          glow: "rgba(14, 165, 233, 0.43)",
          soft: "rgba(14, 165, 233, 0.15)",
          border: "rgba(56, 189, 248, 0.48)",
        },
      },
      {
        to: "/help",
        label: "Help & Support",
        icon: FiHelpCircle,
        moduleKey: "help",
        theme: {
          from: "#e11d48",
          to: "#f97316",
          glow: "rgba(244, 63, 94, 0.42)",
          soft: "rgba(244, 63, 94, 0.15)",
          border: "rgba(251, 113, 133, 0.48)",
        },
      },
    ],
  },
];

const defaultTheme = menuGroups[0].items[0].theme;

const getInitials = (name = "User") => {
  return String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const formatRoleName = (role = "") => {
  return String(role)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const isPathActive = (pathname, route) => {
  if (route === "/") {
    return pathname === "/";
  }

  return pathname === route || pathname.startsWith(`${route}/`);
};

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const user = useCurrentUser();

  const visibleGroups = useMemo(() => {
    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          hasPermissionForUser(user, item.moduleKey, "read")
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [user]);

  const currentTheme = useMemo(() => {
    const allItems = menuGroups.flatMap((group) => group.items);

    const activeItem = allItems.find((item) =>
      isPathActive(location.pathname, item.to)
    );

    return activeItem?.theme || defaultTheme;
  }, [location.pathname]);

  const displayRole =
    user?.roleName ||
    user?.assignedRole?.name ||
    user?.accessRole?.name ||
    formatRoleName(user?.role || "");

  const canOpenProfile =
    hasPermissionForUser(
      user,
      "profile",
      "read"
    );

  const handleLogout = () => {
    clearAuthSession();
    setCurrentUser(null);

    setShowLogoutModal(false);
    navigate("/login", { replace: true });
  };

  return (
    <>
      <style>{`
        @keyframes sidebar-shine {
          0% {
            transform: translateX(-160%) skewX(-20deg);
          }

          45%,
          100% {
            transform: translateX(260%) skewX(-20deg);
          }
        }

        @keyframes sidebar-ship-float {
          0%,
          100% {
            transform: translateY(0px);
          }

          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes sidebar-wave-move-one {
          from {
            transform: translateX(0px);
          }

          to {
            transform: translateX(-36px);
          }
        }

        @keyframes sidebar-wave-move-two {
          from {
            transform: translateX(-22px);
          }

          to {
            transform: translateX(14px);
          }
        }

        @keyframes sidebar-pulse {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }

          50% {
            opacity: 0.45;
            transform: scale(1.65);
          }
        }

        @keyframes sidebar-orb-one {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(14px, 20px, 0) scale(1.1);
          }
        }

        @keyframes sidebar-orb-two {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(-18px, -14px, 0) scale(1.12);
          }
        }

        .modern-sidebar-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(148, 163, 184, 0.25) transparent;
        }

        .modern-sidebar-scrollbar::-webkit-scrollbar {
          width: 5px;
        }

        .modern-sidebar-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .modern-sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.24);
          border-radius: 999px;
        }

        .sidebar-nav-item {
          isolation: isolate;
        }

        .sidebar-nav-item::before {
          position: absolute;
          inset: 0;
          z-index: 0;
          content: "";
          border-radius: inherit;
          background: linear-gradient(
            100deg,
            transparent 5%,
            var(--item-soft-color) 50%,
            transparent 95%
          );
          opacity: 0;
          transform: translateX(-110%);
          transition:
            opacity 220ms ease,
            transform 500ms ease;
        }

        .sidebar-nav-item:hover::before {
          opacity: 1;
          transform: translateX(0);
        }

        .sidebar-active-shine {
          position: absolute;
          top: -40%;
          bottom: -40%;
          left: -35%;
          z-index: 0;
          width: 32%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.22),
            transparent
          );
          animation: sidebar-shine 4.5s ease-in-out infinite;
          pointer-events: none;
        }

        .sidebar-ship {
          transform-origin: center;
          animation: sidebar-ship-float 4s ease-in-out infinite;
        }

        .sidebar-wave-one {
          animation: sidebar-wave-move-one 5s linear infinite alternate;
        }

        .sidebar-wave-two {
          animation: sidebar-wave-move-two 6s linear infinite alternate;
        }

        .sidebar-online-pulse {
          animation: sidebar-pulse 1.8s ease-in-out infinite;
        }

        .sidebar-orb-one {
          animation: sidebar-orb-one 8s ease-in-out infinite;
        }

        .sidebar-orb-two {
          animation: sidebar-orb-two 10s ease-in-out infinite;
        }
      `}</style>

      <aside
        className="fixed left-0 top-0 z-50 flex h-screen w-64 shrink-0 flex-col overflow-hidden border-r border-white/10 text-white shadow-[12px_0_40px_rgba(15,23,42,0.18)]"
        style={{
          background:
            "linear-gradient(180deg, #020b24 0%, #06183b 48%, #07152f 100%)",
        }}
      >
        {/* Background decoration */}
        <div
          className="sidebar-orb-one pointer-events-none absolute -left-20 top-20 h-52 w-52 rounded-full opacity-20 blur-3xl"
          style={{ background: currentTheme.from }}
        />

        <div
          className="sidebar-orb-two pointer-events-none absolute -right-24 bottom-40 h-56 w-56 rounded-full opacity-15 blur-3xl"
          style={{ background: currentTheme.to }}
        />

        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex min-h-[88px] items-center gap-3 border-b border-white/10 px-5">
          <div
            className="relative grid h-12 w-12 shrink-0 place-items-center rounded-2xl shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${currentTheme.from}, ${currentTheme.to})`,
              boxShadow: `0 10px 30px ${currentTheme.glow}`,
            }}
          >
            <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/20 to-transparent" />

            <svg
              className="relative h-7 w-7"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M16 2.5L28 9.25V22.75L16 29.5L4 22.75V9.25L16 2.5Z"
                fill="rgba(255,255,255,0.24)"
                stroke="white"
                strokeWidth="1.5"
              />

              <path
                d="M10 12.1L16 8.7L22 12.1L16 15.5L10 12.1Z"
                fill="white"
              />

              <path
                d="M10 13.7L15.15 16.6V22.6L10 19.7V13.7Z"
                fill="rgba(255,255,255,0.75)"
              />

              <path
                d="M22 13.7L16.85 16.6V22.6L22 19.7V13.7Z"
                fill="rgba(255,255,255,0.45)"
              />
            </svg>
          </div>

          <div className="min-w-0">
            <div className="truncate text-[18px] font-bold tracking-tight text-white">
             Meraki Logistics
            </div>

            <div className="mt-0.5 truncate text-[10px] font-medium tracking-wide text-slate-400">
              Smart. Reliable. Delivered.
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="modern-sidebar-scrollbar relative z-10 flex-1 overflow-y-auto px-3 py-5">
          {visibleGroups.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-slate-400">
              No menu access assigned
            </div>
          ) : (
            <div className="space-y-6">
              {visibleGroups.map((group) => (
                <div key={group.title}>
                  <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                    {group.title}
                  </p>

                  <div className="space-y-1.5">
                    {group.items.map(
                      ({ to, label, icon: Icon, theme }) => (
                        <NavLink
                          key={to}
                          to={to}
                          end={to === "/"}
                          className={({ isActive }) =>
                            [
                              "sidebar-nav-item group relative flex min-h-[48px] items-center gap-3 overflow-hidden rounded-xl border px-2.5 py-2 text-[14px] font-semibold transition-all duration-300",
                              isActive
                                ? "translate-x-0.5 border-white/20 text-white"
                                : "border-transparent text-slate-300 hover:translate-x-1 hover:border-white/10 hover:bg-white/[0.055] hover:text-white",
                            ].join(" ")
                          }
                          style={({ isActive }) => ({
                            "--item-soft-color": theme.soft,
                            ...(isActive
                              ? {
                                  background: `linear-gradient(110deg, ${theme.from}, ${theme.to})`,
                                  borderColor: theme.border,
                                  boxShadow: `0 10px 26px ${theme.glow}`,
                                }
                              : {}),
                          })}
                        >
                          {({ isActive }) => (
                            <>
                              {isActive ? (
                                <span className="sidebar-active-shine" />
                              ) : null}

                              <span
                                className={[
                                  "relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-xl border transition-all duration-300",
                                  isActive
                                    ? "border-white/20 bg-white/15 text-white shadow-inner"
                                    : "border-white/[0.06] bg-white/[0.035] text-slate-400 group-hover:border-white/10 group-hover:bg-white/10 group-hover:text-white",
                                ].join(" ")}
                              >
                                <Icon className="text-[19px]" />
                              </span>

                              <span className="relative z-10 min-w-0 flex-1 truncate">
                                {label}
                              </span>

                              {isActive ? (
                                <span className="relative z-10 flex h-5 w-5 items-center justify-center">
                                  <span className="absolute h-4 w-4 rounded-full bg-white/20" />
                                  <span className="h-2 w-2 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.95)]" />
                                </span>
                              ) : (
                                <span
                                  className="relative z-10 h-1.5 w-1.5 rounded-full opacity-0 transition-all duration-300 group-hover:opacity-100"
                                  style={{ background: theme.to }}
                                />
                              )}
                            </>
                          )}
                        </NavLink>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Animated shipping scene */}
        <div className="relative z-10 h-[82px] shrink-0 overflow-hidden border-t border-white/[0.06] px-2">
          <svg
            viewBox="0 0 240 82"
            className="h-full w-full"
            fill="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="sidebarShipGradient"
                x1="0"
                y1="0"
                x2="1"
                y2="1"
              >
                <stop stopColor={currentTheme.from} />
                <stop offset="1" stopColor={currentTheme.to} />
              </linearGradient>

              <linearGradient
                id="sidebarSeaGradient"
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop stopColor={currentTheme.from} stopOpacity="0" />
                <stop
                  offset="0.5"
                  stopColor={currentTheme.to}
                  stopOpacity="0.7"
                />
                <stop
                  offset="1"
                  stopColor={currentTheme.from}
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>

            <g className="sidebar-ship">
              <path
                d="M43 55H188L176 68H63L43 55Z"
                fill="url(#sidebarShipGradient)"
                opacity="0.94"
              />

              <path
                d="M68 42H153V55H68V42Z"
                fill="rgba(255,255,255,0.12)"
                stroke="rgba(255,255,255,0.25)"
              />

              <rect
                x="75"
                y="30"
                width="23"
                height="12"
                rx="2"
                fill={currentTheme.from}
              />

              <rect
                x="100"
                y="30"
                width="23"
                height="12"
                rx="2"
                fill={currentTheme.to}
              />

              <rect
                x="125"
                y="30"
                width="23"
                height="12"
                rx="2"
                fill="#f97316"
              />

              <rect
                x="88"
                y="17"
                width="22"
                height="13"
                rx="2"
                fill={currentTheme.to}
              />

              <rect
                x="112"
                y="17"
                width="22"
                height="13"
                rx="2"
                fill={currentTheme.from}
              />

              <path
                d="M58 41H68V55H52L58 41Z"
                fill="rgba(255,255,255,0.80)"
              />

              <rect
                x="60"
                y="33"
                width="5"
                height="8"
                rx="1"
                fill="rgba(255,255,255,0.82)"
              />

              <circle
                cx="177"
                cy="60"
                r="2.5"
                fill="white"
                style={{
                  filter: `drop-shadow(0 0 6px ${currentTheme.to})`,
                }}
              />
            </g>

            <path
              className="sidebar-wave-one"
              d="M-20 71C0 63 18 78 39 71C60 64 78 78 99 71C120 64 139 78 160 71C181 64 200 78 221 71C242 64 260 77 280 71"
              stroke="url(#sidebarSeaGradient)"
              strokeWidth="2.4"
              strokeLinecap="round"
            />

            <path
              className="sidebar-wave-two"
              d="M-25 77C-4 70 14 83 35 77C56 70 74 83 95 77C116 70 135 83 156 77C177 70 196 83 217 77C238 70 258 82 278 77"
              stroke={currentTheme.from}
              strokeOpacity="0.28"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* User and logout */}
        <div className="relative z-10 shrink-0 border-t border-white/10 p-3">
          <button
  type="button"
  onClick={() => {
    if (canOpenProfile) {
      navigate("/profile");
    }
  }}
  disabled={!canOpenProfile}
  title="Open Profile"
  aria-label="Open your profile"
  className="group mb-2 flex w-full cursor-pointer items-center gap-3 rounded-2xl border bg-white/[0.055] p-3 text-left backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.09] focus:outline-none focus:ring-2 focus:ring-white/20 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
  style={{
    borderColor: currentTheme.border,
    boxShadow: `0 10px 30px ${currentTheme.soft}`,
  }}
>
  <div
    className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-xs font-bold text-white shadow-lg transition-transform duration-300 group-hover:scale-105"
    style={{
      background: `linear-gradient(135deg, ${currentTheme.from}, ${currentTheme.to})`,
      boxShadow: `0 8px 22px ${currentTheme.glow}`,
    }}
  >
    {getInitials(user?.name || "Admin")}
  </div>

  <div className="min-w-0 flex-1">
    <div className="truncate text-sm font-bold text-white">
      {user?.name || "Admin"}
    </div>

    <div className="truncate text-[11px] font-medium text-slate-400">
      {displayRole || "User"}
    </div>

    <div className="mt-1 flex items-center gap-2 text-[11px] font-medium text-emerald-400">
      <span className="relative flex h-2 w-2">
        <span className="sidebar-online-pulse absolute inset-0 rounded-full bg-emerald-400" />
        <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
      </span>

      Online
    </div>
  </div>

  <FiMoreVertical className="text-lg text-slate-500 transition-all duration-300 group-hover:text-white" />
</button>

          <button
            type="button"
            onClick={() => setShowLogoutModal(true)}
            className="group flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-sm font-semibold text-slate-400 transition-all duration-300 hover:border-red-400/20 hover:bg-red-500/10 hover:text-red-300"
          >
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/[0.035] transition group-hover:bg-red-500/15">
              <FiLogOut className="text-[17px]" />
            </span>

            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Logout modal */}
      {showLogoutModal ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-md">
          <div className="w-full max-w-[390px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div
              className="h-1.5 w-full"
              style={{
                background: `linear-gradient(90deg, ${currentTheme.from}, ${currentTheme.to})`,
              }}
            />

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
                  <FiLogOut className="text-xl" />
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    Confirm Logout
                  </h2>

                  <p className="mt-1.5 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Are you sure you want to log out from your account?
                  </p>
                </div>
              </div>

              <div className="mt-7 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogoutModal(false)}
                  className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition hover:-translate-y-0.5 hover:bg-red-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}